import {useEffect, useState} from 'react'
import {sanityClient, sanityEnabled, urlFor} from '../lib/sanity'
import {siteConfig as fallbackConfig} from '../config/siteConfig'

/**
 * Fetches all CMS content from Sanity in a single batched query.
 * Returns `null` during loading; returns the fallback config on error or when Sanity is disabled.
 */
export const useSiteContent = () => {
    const [data, setData] = useState(sanityEnabled ? null : fallbackConfig)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!sanityEnabled || !sanityClient) {
            setData(fallbackConfig)
            return
        }

        const query = `{
            "brand": *[_type == "brand"][0],
            "hero": *[_type == "hero"][0],
            "servicesList": *[_type == "servicesList"][0],
            "about": *[_type == "about"][0],
            "gallery": *[_type == "gallery"][0],
            "testimonials": *[_type == "testimonials"][0],
            "contact": *[_type == "contact"][0],
            "hours": *[_type == "openingHours"][0]
        }`

        sanityClient
            .fetch(query)
            .then((res) => {
                setData(mapSanityToSiteConfig(res))
            })
            .catch((err) => {
                console.error('[Sanity] fetch failed, using fallback', err)
                setError(err)
                setData(fallbackConfig)
            })
    }, [])

    return {data, error, loading: data === null}
}

/**
 * Normalises Sanity documents into the same shape as siteConfig.js,
 * so all existing components keep working unchanged.
 */
const mapSanityToSiteConfig = (res) => {
    const {brand, hero, servicesList, about, gallery, testimonials, contact, hours} =
        res || {}

    // Gallery: map Sanity image refs to resolvable URLs
    const galleryImages = (gallery?.images || [])
        .map((item) => {
            const url = urlFor(item.image)?.width(1400).url()
            if (!url) return null
            return {url, alt: item.alt || ''}
        })
        .filter(Boolean)

    const services = (servicesList?.services || []).map((s) => ({
        id: s.id?.current || s._key,
        name: s.name,
        description: s.description || '',
        duration: s.duration,
        price: s.price,
    }))

    const aboutImage = about?.image ? urlFor(about.image)?.width(900).url() : null

    return {
        brand: {
            name: brand?.name || fallbackConfig.brand.name,
            tagline: brand?.tagline || fallbackConfig.brand.tagline,
            monogram: brand?.monogram || fallbackConfig.brand.monogram,
        },
        hero: {
            eyebrow: hero?.eyebrow ?? fallbackConfig.hero.eyebrow,
            title: hero?.title || fallbackConfig.hero.title,
            titleAccent: hero?.titleAccent ?? fallbackConfig.hero.titleAccent,
            subtitle: hero?.subtitle || fallbackConfig.hero.subtitle,
        },
        about: {
            title: about?.title || fallbackConfig.about.title,
            body: about?.body || fallbackConfig.about.body,
            years: about?.years ?? fallbackConfig.about.years,
            yearsLabel: about?.yearsLabel ?? fallbackConfig.about.yearsLabel,
            clients: about?.clients ?? fallbackConfig.about.clients,
            clientsLabel:
                about?.clientsLabel ?? fallbackConfig.about.clientsLabel,
            image: aboutImage,
        },
        services: services.length > 0 ? services : fallbackConfig.services,
        gallery:
            galleryImages.length > 0 ? galleryImages : fallbackConfig.gallery,
        testimonials:
            testimonials?.items && testimonials.items.length > 0
                ? {
                      sectionTitle:
                          testimonials.sectionTitle ||
                          fallbackConfig.testimonials.sectionTitle,
                      sectionSubtitle:
                          testimonials.sectionSubtitle ||
                          fallbackConfig.testimonials.sectionSubtitle,
                      items: testimonials.items.map((t) => ({
                          name: t.name,
                          quote: t.quote,
                          rating: t.rating ?? 5,
                          source: t.source || '',
                      })),
                  }
                : fallbackConfig.testimonials,
        contact: {
            whatsapp: contact?.whatsapp || fallbackConfig.contact.whatsapp,
            whatsappDisplay:
                contact?.whatsappDisplay ||
                fallbackConfig.contact.whatsappDisplay,
            phone: contact?.phone || fallbackConfig.contact.phone,
            email: contact?.email ?? '',
            address: contact?.address || fallbackConfig.contact.address,
            mapsEmbed: contact?.mapsEmbed || fallbackConfig.contact.mapsEmbed,
            instagram: contact?.instagram || fallbackConfig.contact.instagram,
            instagramHandle:
                contact?.instagramHandle ||
                fallbackConfig.contact.instagramHandle,
        },
        hours:
            hours?.rows && hours.rows.length > 0
                ? hours.rows.map((r) => ({days: r.days, time: r.time}))
                : fallbackConfig.hours,
        calcom: {
            username:
                brand?.calcomUsername || fallbackConfig.calcom.username,
        },
    }
}
